import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useZoteroCollections } from "@/hooks/useZotero";
import { useConceptList } from "@/hooks/useConcepts";
import {
  useZoteroManifest,
  useZoteroImportPreview,
  uploadAttachment,
} from "@/hooks/useZoteroImport";
import {
  buildStorageIndex,
  matchManifest,
  type MatchedEntry,
} from "@/lib/zoteroFileMatch";
import { runUploadQueue } from "@/lib/zoteroUploadQueue";
import { loadImportMemory, saveImportMemory } from "@/lib/zoteroImportMemory";
import { cloudAvailability } from "@/lib/zoteroCloudAvailability";
import { useZoteroBulkImport } from "@/hooks/useZoteroBulkImport";

type Step = "collections" | "files" | "preview" | "importing" | "importing-cloud" | "done";

export function ZoteroImportWizard({
  open,
  onClose,
  defaultConceptId,
}: {
  open: boolean;
  onClose: () => void;
  defaultConceptId?: string | null;
}) {
  const mem = loadImportMemory();
  const qc = useQueryClient();
  const collections = useZoteroCollections();
  const { data: concepts } = useConceptList();
  const manifest = useZoteroManifest();
  const preview = useZoteroImportPreview();
  const bulk = useZoteroBulkImport(); // { start, cancel, state }
  const folderInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("collections");
  const [selected, setSelected] = useState<string[]>(mem?.collectionKeys ?? []);
  const [files, setFiles] = useState<File[]>([]);
  const [conceptId, setConceptId] = useState<string | null>(
    defaultConceptId ?? mem?.conceptId ?? null,
  );
  const [role, setRole] = useState<"trawled" | "foundational">(mem?.role ?? "trawled");
  const [matched, setMatched] = useState<MatchedEntry[]>([]);
  const [newKeys, setNewKeys] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState<{ succeeded: number; failed: number } | null>(null);
  const [cloudPhase, setCloudPhase] = useState(false);
  const [localSummary, setLocalSummary] = useState({ succeeded: 0, failed: 0 });

  // Collections are fetched lazily (enabled: false) — trigger on open.
  useEffect(() => {
    if (open && !collections.data && !collections.isLoading) {
      collections.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps — only on open transition
  }, [open]);

  const tree = collections.data?.collections ?? [];

  // Group picked files by their top-level folder name. webkitdirectory only
  // exposes webkitRelativePath (e.g. "Zotero/storage/AB/x.pdf"), so the root
  // segment is the chosen folder's *name* — the absolute path is withheld by
  // the browser. This still disambiguates multiple selections.
  const chosenFolders = useMemo(() => {
    const byRoot = new Map<string, number>();
    for (const f of files) {
      const root = (f.webkitRelativePath || f.name).split("/")[0] || "(folder)";
      byRoot.set(root, (byRoot.get(root) ?? 0) + 1);
    }
    return [...byRoot.entries()].map(([name, count]) => ({ name, count }));
  }, [files]);

  // Step 1 → fetch manifest for selected collections (+ descendants)
  async function toFiles() {
    const keys = expandWithDescendants(selected, tree);
    await manifest.mutateAsync(keys);
    setStep("files");
  }

  // Step 2 → match files, run dedup preview. `filesOverride` lets Skip force
  // an empty file set so nothing matches and everything routes to the cloud.
  async function toPreview(filesOverride?: File[]) {
    const effectiveFiles = filesOverride ?? files;
    const index = buildStorageIndex(effectiveFiles);
    const entries = manifest.data?.entries ?? [];
    const m = matchManifest(entries, index);
    setMatched(m);
    const res = await preview.mutateAsync(entries);
    setNewKeys(new Set(res.results.filter((r) => r.status === "new").map((r) => r.attachment_key)));
    setStep("preview");
  }

  const toImport = useMemo(
    () => matched.filter((m) => m.file && newKeys.has(m.entry.attachment_key)),
    [matched, newKeys],
  );

  // New, no local file → split into cloud-fetchable vs no-source.
  const newNoFileEntries = matched.filter(
    (m) => !m.file && newKeys.has(m.entry.attachment_key),
  );
  const cloudKeys = newNoFileEntries
    .filter((m) => cloudAvailability(m.entry) === "cloud")
    .map((m) => m.entry.attachment_key);

  async function runImport() {
    if (!conceptId) return;
    saveImportMemory({ collectionKeys: selected, conceptId, role });
    setStep("importing");

    let local = { succeeded: 0, failed: 0 };
    if (toImport.length) {
      setProgress({ done: 0, total: toImport.length });
      local = await runUploadQueue({
        items: toImport,
        concurrency: 5,
        uploadOne: (m) => uploadAttachment(m, conceptId!, role),
        onProgress: (done, total) => setProgress({ done, total }),
      });
    }
    setLocalSummary(local);

    if (cloudKeys.length) {
      setStep("importing-cloud");
      setCloudPhase(true);
      const started = await bulk.start({
        collection_key: null,
        concept_id: conceptId,
        role,
        attachment_keys: cloudKeys,
        collection_keys: expandWithDescendants(selected, tree),
      });
      if (!started) {
        // Cloud job never started (409 / network) — the terminal-watching
        // effect will never fire, so flush the already-succeeded local
        // uploads into the summary here.
        setCloudPhase(false);
        finishImport(local, { added: 0, failed: 0 });
      }
      return; // success path: completion handled by the terminal-watching effect below
    }

    finishImport(local, { added: 0, failed: 0 });
  }

  function finishImport(
    local: { succeeded: number; failed: number },
    cloud: { added: number; failed: number },
  ) {
    setSummary({ succeeded: local.succeeded + cloud.added, failed: local.failed + cloud.failed });
    qc.invalidateQueries({ queryKey: ["papers"] });
    qc.invalidateQueries({ queryKey: ["paper-keys"] });
    qc.invalidateQueries({ queryKey: ["concept-papers"] });
    qc.invalidateQueries({ queryKey: ["zotero-sync-events"] });
    setStep("done");
  }

  // Watch the bulk-import job for a terminal status during the cloud phase,
  // then fold its results into the final summary.
  useEffect(() => {
    if (!cloudPhase) return;
    const s = bulk.state.status;
    if (s === "done" || s === "cancelled" || s === "error") {
      setCloudPhase(false);
      finishImport(localSummary, {
        added: bulk.state.added ?? 0,
        failed: bulk.state.failed ?? 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudPhase, bulk.state.status]);

  if (!open) return null;

  // Five DISJOINT buckets that partition `matched` (sum === matched.length).
  const counts = {
    newWithFile: toImport.length, // new && local file → upload queue
    newCloud: cloudKeys.length, // new && no local file && cloud-available → SSE job
    newNoSource: newNoFileEntries.length - cloudKeys.length, // new && neither
    inLibraryWithFile: matched.filter((m) => m.file && !newKeys.has(m.entry.attachment_key)).length,
    inLibraryNoFile: matched.filter((m) => !m.file && !newKeys.has(m.entry.attachment_key)).length,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-background p-5 shadow-lg">
        <header className="flex items-center justify-between">
          <h2 className="text-base font-medium">Import from Zotero</h2>
          <button onClick={onClose} className="text-sm text-muted-foreground">Close</button>
        </header>

        {step !== "importing" && step !== "importing-cloud" && step !== "done" && (
          <p className="text-xs font-medium text-muted-foreground">
            {step === "collections" && "Step 1 of 3 · Choose collections"}
            {step === "files" && "Step 2 of 3 · Choose files"}
            {step === "preview" && "Step 3 of 3 · Review & import"}
          </p>
        )}

        {step === "collections" && (
          <section className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose collections from your Zotero <span className="font-medium">cloud</span> library
              (synced via the Web API). The next step will let you pick a local folder as a source for
              the full PDF files.
            </p>
            {/* Fixed-height list area: skeleton rows fill it during load so the
                panel doesn't reflow/resize when the collections arrive. */}
            <div className="max-h-72 min-h-[15rem] overflow-auto rounded-md border p-2">
              {collections.isLoading ? (
                <ul className="space-y-2" aria-busy="true" aria-label="Loading collections">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
                      <Skeleton className="h-4" style={{ width: `${70 - i * 6}%` }} />
                    </li>
                  ))}
                </ul>
              ) : tree.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">No collections found.</p>
              ) : (
                <ul className="space-y-1">
                  {tree.map((c) => (
                    <li key={c.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.key)}
                        onChange={(e) =>
                          setSelected((s) => (e.target.checked ? [...s, c.key] : s.filter((k) => k !== c.key)))
                        }
                      />
                      {c.name} <span className="text-xs text-muted-foreground">({c.item_count})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button disabled={!selected.length || manifest.isPending} onClick={toFiles}>
              {manifest.isPending ? "Reading library…" : "Next: Choose PDF locations"}
            </Button>
          </section>
        )}

        {step === "files" && (
          <section className="space-y-3">
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              // @ts-expect-error non-standard but widely supported
              webkitdirectory=""
              multiple
              onChange={(e) => {
                // Capture the picked files synchronously BEFORE clearing value —
                // setting value="" empties e.target.files, and the setFiles
                // updater runs after this handler returns.
                const picked = Array.from(e.target.files ?? []);
                setFiles((prev) => [...prev, ...picked]);
                e.target.value = ""; // allow re-picking the same/another folder
              }}
            />

            {/* Section A — local PDFs */}
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Local PDF files</p>
              <p className="text-xs text-muted-foreground">
                Pick your Zotero data folder — the one containing a <code>storage/</code> folder.
              </p>
              <div>
                <p className="text-xs text-muted-foreground">Default locations:</p>
                <ul className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
                  <li>
                    macOS: <code>{"~/Zotero"}</code>
                  </li>
                  <li>
                    Windows: <code>{"C:\\Users\\<you>\\Zotero"}</code>
                  </li>
                </ul>
              </div>
              <div className="flex items-center gap-3 pt-0.5">
                <Button variant="outline" size="sm" onClick={() => folderInputRef.current?.click()}>
                  {files.length ? "Add another folder" : "Choose folder"}
                </Button>
                {files.length > 0 && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline transition-colors hover:text-foreground"
                    onClick={() => setFiles([])}
                  >
                    Clear &amp; choose a different folder
                  </button>
                )}
              </div>
              {chosenFolders.length > 0 ? (
                <div className="space-y-0.5 pt-1 text-xs">
                  <p className="text-muted-foreground">
                    Chosen folder{chosenFolders.length > 1 ? "s" : ""}:
                  </p>
                  <ul className="space-y-0.5">
                    {chosenFolders.map((f) => (
                      <li key={f.name} className="flex items-center justify-between gap-2">
                        <code className="truncate">{f.name}/</code>
                        <span className="shrink-0 text-muted-foreground">
                          {f.count.toLocaleString()} files
                        </span>
                      </li>
                    ))}
                  </ul>
                  {chosenFolders.length > 1 && (
                    <p className="text-muted-foreground">
                      {files.length.toLocaleString()} files indexed total
                    </p>
                  )}
                </div>
              ) : (
                <p className="pt-1 text-xs text-muted-foreground">No folder chosen yet.</p>
              )}
            </div>

            {/* Section B — cloud */}
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Or fetch from Zotero cloud</p>
              <p className="text-xs text-muted-foreground">
                No local PDFs? Pull the full files straight from your Zotero cloud library instead.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={preview.isPending}
                onClick={() => toPreview([])}
              >
                Skip — fetch all from Zotero cloud
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("collections")}>← Back</Button>
              <Button
                className="min-w-[7.5rem]"
                disabled={!files.length || preview.isPending}
                onClick={() => toPreview()}
              >
                {preview.isPending ? "Checking…" : "Next: preview"}
              </Button>
            </div>
          </section>
        )}

        {step === "preview" && (
          <section className="space-y-3 text-sm">
            <ul className="space-y-1">
              <li>{counts.newWithFile} new — local upload</li>
              <li>{counts.newCloud} new — from Zotero cloud</li>
              <li className="text-muted-foreground">{counts.newNoSource} no source (skipped)</li>
              <li className="text-muted-foreground">
                {counts.inLibraryWithFile + counts.inLibraryNoFile} already in your library
              </li>
            </ul>
            <label className="block">Concept
              <select className="mt-1 h-8 w-full rounded-md border px-2"
                value={conceptId ?? ""} onChange={(e) => setConceptId(e.target.value || null)}>
                <option value="">Pick a concept…</option>
                {(concepts ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <fieldset className="flex gap-3">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={role === "trawled"} onChange={() => setRole("trawled")} /> Trawled
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={role === "foundational"} onChange={() => setRole("foundational")} /> Seed
              </label>
            </fieldset>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("files")}>← Back</Button>
              <Button
                disabled={!conceptId || (!counts.newWithFile && !counts.newCloud)}
                onClick={runImport}
              >
                Import {counts.newWithFile + counts.newCloud}
              </Button>
            </div>
          </section>
        )}

        {step === "importing" && (
          <section className="space-y-2 text-sm">
            <p>Importing… {progress.done}/{progress.total}</p>
            <div className="h-2 w-full rounded bg-muted">
              <div className="h-2 rounded bg-primary"
                style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
          </section>
        )}

        {step === "importing-cloud" && (
          <section className="space-y-2 text-sm">
            <p>
              Fetching from Zotero cloud… added {bulk.state.added ?? 0}, failed{" "}
              {bulk.state.failed ?? 0}
            </p>
            <Button variant="ghost" size="sm" onClick={() => bulk.cancel()}>Cancel</Button>
          </section>
        )}

        {step === "done" && summary && (
          <section className="space-y-3 text-sm">
            <p>{summary.succeeded} imported{summary.failed ? `, ${summary.failed} failed` : ""}.</p>
            <Button onClick={onClose}>Done</Button>
          </section>
        )}
      </div>
    </div>
  );
}

// Expand selected collection keys to include descendants using the parent_key tree.
function expandWithDescendants(
  keys: string[],
  tree: { key: string; parent_key: string | null }[],
): string[] {
  const childrenOf = new Map<string, string[]>();
  for (const c of tree) {
    if (c.parent_key) childrenOf.set(c.parent_key, [...(childrenOf.get(c.parent_key) ?? []), c.key]);
  }
  const out = new Set<string>();
  const stack = [...keys];
  while (stack.length) {
    const k = stack.pop()!;
    if (out.has(k)) continue;
    out.add(k);
    stack.push(...(childrenOf.get(k) ?? []));
  }
  return [...out];
}
